/**
 * A single-line text field whose dynamic values ("{date}") show as pills, with
 * a "+ Value" button that opens a searchable menu of the values to insert
 * (lib/dynamicTokens). Used for every name a template creates on the board:
 * group title, item names, subitem names.
 *
 * The value stays a plain string ("Sprint {date}"), so storage, validation
 * and the apply are unchanged; the pills exist only on screen.
 *
 * Built on a contentEditable line: text is typed as usual, a pill is one
 * non-editable span that Backspace removes whole. The DOM is rebuilt from
 * `value` only when the value changes from outside, so typing never jumps
 * the caret.
 *
 *   variant 'input'  bordered, like a form field (group title)
 *   variant 'bare'   borderless, inside a table cell; the button shows on hover
 */
import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { groupedTokens, previewContext, tokenDef, tokenSegments } from '../../lib/dynamicTokens';
import { resolveName } from '../../lib/dynamicValues';
import AnchoredPopover from '../TemplateEditor/AnchoredPopover';
import './DynamicTextField.css';

/** A token's pill element. Also the read-only rendering (TokenText). */
function pillNode(token) {
  const def = tokenDef(token);
  const span = document.createElement('span');
  span.className = 'spt-dtf-pill';
  span.setAttribute('contenteditable', 'false');
  span.dataset.token = token;
  span.textContent = def ? def.label : token;
  if (def) span.title = def.description;
  return span;
}

function renderInto(el, value) {
  el.textContent = '';
  for (const seg of tokenSegments(value)) {
    el.appendChild(seg.kind === 'token' ? pillNode(seg.token) : document.createTextNode(seg.text));
  }
}

/** The stored string for a node or fragment: text as typed, pills as their token. */
export function serializeNode(node) {
  let out = '';
  node.childNodes.forEach((child) => {
    if (child.nodeType === 3) out += child.nodeValue;
    else if (child.nodeType === 1) {
      if (child.dataset && child.dataset.token) out += child.dataset.token;
      else if (child.nodeName !== 'BR') out += serializeNode(child);
    }
  });
  return out.replace(/ /g, ' ').replace(/[\r\n]+/g, ' ');
}

/** The caret's range if it is inside `el`, else a collapsed range at its end. */
function caretRange(el, saved) {
  const sel = window.getSelection && window.getSelection();
  if (sel && sel.rangeCount && el.contains(sel.getRangeAt(0).startContainer)) return sel.getRangeAt(0);
  if (saved && el.contains(saved.startContainer)) return saved;
  const r = document.createRange();
  r.selectNodeContents(el);
  r.collapse(false);
  return r;
}

function placeCaretAfter(node) {
  const sel = window.getSelection && window.getSelection();
  if (!sel) return null;
  const r = document.createRange();
  r.setStartAfter(node);
  r.collapse(true);
  sel.removeAllRanges();
  sel.addRange(r);
  return r;
}

/** The text on either side of a range, as stored. */
function around(el, range) {
  const before = document.createRange();
  before.selectNodeContents(el);
  before.setEnd(range.startContainer, range.startOffset);
  const after = document.createRange();
  after.selectNodeContents(el);
  after.setStart(range.endContainer, range.endOffset);
  return { before: serializeNode(before.cloneContents()), after: serializeNode(after.cloneContents()) };
}

export function TokenMenu({ onPick, onClose }) {
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const ctx = useMemo(() => previewContext(), []);
  const groups = groupedTokens(query);
  const flat = groups.flatMap((g) => g.items);
  const pick = (def) => def && onPick(def.token);

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((i) => Math.min(i + 1, flat.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      pick(flat[active]);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    }
  };

  let index = -1;
  return (
    <div className="spt-dtf-menu" onKeyDown={onKeyDown}>
      <input
        className="spt-dtf-search"
        placeholder="Search values"
        aria-label="Search dynamic values"
        value={query}
        autoFocus
        onChange={(e) => {
          setQuery(e.target.value);
          setActive(0);
        }}
      />
      {groups.length === 0 && <div className="spt-dtf-empty">No value matches “{query}”.</div>}
      <div role="listbox" aria-label="Dynamic values">
        {groups.map((g) => (
          <div key={g.group}>
            <div className="spt-dtf-group">{g.group}</div>
            {g.items.map((def) => {
              index += 1;
              const i = index;
              return (
                <div
                  key={def.token}
                  role="option"
                  aria-selected={i === active}
                  title={def.description}
                  className={`spt-dtf-option ${i === active ? 'spt-dtf-option-on' : ''}`}
                  onMouseEnter={() => setActive(i)}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => pick(def)}
                >
                  <span>{def.label}</span>
                  <span className="spt-dtf-option-preview">{def.preview(ctx)}</span>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function DynamicTextField({
  value,
  onChange,
  placeholder,
  variant = 'input',
  id,
  ariaLabel,
  inputRef, // gets the editable element, so a caller can focus() it
  invalid = false,
  showPreview = false, // "Preview: Sprint Oct 14, 2026" under the field
  onEnter, // Enter in the field (a line has no newlines)
  adornment, // shown right after the text (bare): e.g. an item's subitem count
}) {
  const editorRef = useRef(null);
  const buttonRef = useRef(null);
  const lastValue = useRef(null);
  const savedRange = useRef(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const ctx = useMemo(() => previewContext(), []);

  const setEditor = useCallback(
    (node) => {
      editorRef.current = node;
      if (inputRef) {
        if (typeof inputRef === 'function') inputRef(node);
        else inputRef.current = node;
      }
    },
    [inputRef],
  );

  // Rebuild the line only when the value changed from outside.
  useLayoutEffect(() => {
    const el = editorRef.current;
    const v = String(value == null ? '' : value);
    if (el && v !== lastValue.current) {
      renderInto(el, v);
      lastValue.current = v;
    }
  }, [value]);

  const emit = () => {
    const el = editorRef.current;
    let v = serializeNode(el);
    if (!v && el.childNodes.length) el.textContent = ''; // drop a stray <br> so the placeholder shows
    lastValue.current = v;
    onChange(v);
  };

  const saveRange = () => {
    const sel = window.getSelection && window.getSelection();
    if (sel && sel.rangeCount && editorRef.current && editorRef.current.contains(sel.getRangeAt(0).startContainer)) {
      savedRange.current = sel.getRangeAt(0).cloneRange();
    }
  };

  /** Inserts nodes at the caret (replacing a selection) and puts the caret after them. */
  const insertNodes = (make) => {
    const el = editorRef.current;
    const range = caretRange(el, savedRange.current);
    const { before, after } = around(el, range);
    range.deleteContents();
    const nodes = make({ before, after });
    const frag = document.createDocumentFragment();
    nodes.forEach((n) => frag.appendChild(n));
    const last = nodes[nodes.length - 1];
    range.insertNode(frag);
    el.normalize();
    savedRange.current = placeCaretAfter(last) || savedRange.current;
    emit();
  };

  const insertToken = (token) => {
    setMenuOpen(false);
    editorRef.current.focus();
    insertNodes(({ before, after }) => {
      const nodes = [];
      if (before && !/\s$/.test(before)) nodes.push(document.createTextNode(' '));
      nodes.push(pillNode(token));
      if (!after || !/^\s/.test(after)) nodes.push(document.createTextNode(' '));
      return nodes;
    });
  };

  const focusEnd = (e) => {
    e.preventDefault();
    const el = editorRef.current;
    el.focus();
    const r = document.createRange();
    r.selectNodeContents(el);
    r.collapse(false);
    const sel = window.getSelection && window.getSelection();
    if (sel) {
      sel.removeAllRanges();
      sel.addRange(r);
    }
    savedRange.current = r;
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (onEnter) onEnter();
    }
  };

  // Plain text only, on one line.
  const onPaste = (e) => {
    e.preventDefault();
    const text = ((e.clipboardData && e.clipboardData.getData('text/plain')) || '').replace(/\s*[\r\n]+\s*/g, ' ');
    if (text) insertNodes(() => [document.createTextNode(text)]);
  };

  return (
    <div className={`spt-dtf spt-dtf--${variant}${invalid ? ' spt-dtf--invalid' : ''}`}>
      <div className="spt-dtf-row">
        <div
          ref={setEditor}
          id={id}
          className="spt-dtf-editor"
          contentEditable
          suppressContentEditableWarning
          tabIndex={0}
          role="textbox"
          aria-label={ariaLabel}
          aria-multiline="false"
          aria-invalid={invalid || undefined}
          data-placeholder={placeholder}
          spellCheck
          onInput={emit}
          onKeyDown={onKeyDown}
          onKeyUp={saveRange}
          onMouseUp={saveRange}
          onBlur={saveRange}
          onPaste={onPaste}
          onDrop={(e) => e.preventDefault()}
        />
        {adornment}
        {variant === 'bare' && (
          // the rest of the cell: a click here puts the caret at the end of the text
          <span className="spt-dtf-rest" onMouseDown={focusEnd} aria-hidden="true" />
        )}
        <button
          ref={buttonRef}
          type="button"
          className="spt-dtf-insert"
          aria-label="Insert a dynamic value"
          aria-haspopup="listbox"
          aria-expanded={menuOpen}
          title="Insert a dynamic value, such as the apply date"
          onMouseDown={(e) => {
            saveRange();
            e.preventDefault(); // keep the caret where it is
          }}
          onClick={() => setMenuOpen((o) => !o)}
        >
          + Value
        </button>
      </div>
      {showPreview && (
        <p className="spt-dtf-preview" aria-live="polite">
          Preview: {resolveName(value, ctx)}
        </p>
      )}
      {menuOpen && (
        <AnchoredPopover anchorEl={buttonRef.current} onClose={() => setMenuOpen(false)} width={280}>
          <TokenMenu onPick={insertToken} onClose={() => setMenuOpen(false)} />
        </AnchoredPopover>
      )}
    </div>
  );
}

/** Read-only: a name with its dynamic values as pills. */
export function TokenText({ value }) {
  return tokenSegments(value).map((seg, i) =>
    seg.kind === 'token' ? (
      <span key={i} className="spt-dtf-pill" title={seg.def ? seg.def.description : undefined}>
        {seg.def ? seg.def.label : seg.token}
      </span>
    ) : (
      <React.Fragment key={i}>{seg.text}</React.Fragment>
    ),
  );
}
